import { isEmpty, isEqual } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import {
  createChannelGroup,
  deleteChannelGroup,
  fetchChannelGroups,
  fetchChannels,
  IChannel,
  IChannelGroup,
  IChannelGroupLabel,
  IChannelGroupRow,
  IChannelGroupsUiParams,
  IChannelRow,
  IChannelsUiParams,
  IGetChannelGroupsAPIParams,
  updateChannelGroup,
} from '~/src/dash/views/channels/api';
import { ChannelGroups } from '~/src/dash/views/channels/ChannelGroups';
import { IChannelGroupsConfig } from '~/src/dash/views/channels/ChannelGroups/config';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

export const ChannelGroupsContainer = () => {
  const { session, user } = useBaseStore();
  const isSuperUser = hasRoleSuperUser(user.user);
  const cfg = useBaseConfig().Channels.Groups as IChannelGroupsConfig;

  const [channels, setChannels] = useState<Partial<IChannelRow>[]>([]);
  const [channelGroups, setChannelGroups] = useState<IChannelGroupRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [labels, setLabels] = useState<IChannelGroupLabel[]>([]);
  const [error, setError] = useState<any>(null);
  const [uiParams, setUiParams] = useState<IChannelGroupsUiParams>({});
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [isActionPending, setIsActionPending] = useState(false);

  // format request params for onFetchChannelGroups
  const formatParams = (params: IChannelGroupsUiParams): IGetChannelGroupsAPIParams => ({
    idSearch: parseInt(params.searchInput || '', 10) || '',
    nameSearch: params.searchInput,
    orderBy: params.orderBy,
    orderDirection: params.order,
    page: params.currPage,
    pageSize: params.rowsPerPage,
    preloads: ['ChannelGroupLabels'],
    uiFields: [],
  });

  // update config for state persistence
  const updateCfg = (params: IChannelGroupsUiParams) => {
    if (params.orderBy) cfg.defaultState.orderBy = params.orderBy;
    if (params.order) cfg.defaultState.orderDirection = params.order;
    if (params.currPage) cfg.defaultState.page = params.currPage;
    if (params.rowsPerPage) cfg.defaultState.rowsPerPage = params.rowsPerPage;
    if (params.searchInput) cfg.defaultState.searchInput = params.searchInput;
    if (params.activeColumnKeys) cfg.defaultState.activeColumnKeys = params.activeColumnKeys;
    if (params.isTableExpanded) cfg.defaultState.isTableExpanded = params.isTableExpanded;
  };

  // format onFetchChannelGroups' response
  const formatChannelGroups = (channelGroups: IChannelGroup[]): IChannelGroupRow[] => {
    const channelGroupRows: IChannelGroupRow[] = [];
    channelGroups.forEach((group: IChannelGroup) => {
      const { id, ChannelIDs, ChannelGroupLabels, Name, DefaultWeight, PublisherID } = group;
      const channelGroupRow: IChannelGroupRow = {
        ID: String(id),
        DefaultWeight,
        PublisherID,
        Name: checkUserInfoMasked(user.user) ? `demo_channel_${id}` : Name, // mask channel info if user is a demo account
        ChannelIDs: String(ChannelIDs).split(','),
        ChannelGroupLabels: Array.isArray(ChannelGroupLabels)
          ? ChannelGroupLabels.map(({ Name, ID }) => ({ ID, Name }))
          : [],
      };
      channelGroupRows.push(channelGroupRow);
    });
    return channelGroupRows;
  };

  // format onFetchChannels' response
  const formatChannels = (channels: Array<Partial<IChannel>>) => {
    return channels.map((channel: Partial<IChannel>) => {
      const { ID, Name } = channel;
      return {
        ID,
        Name: checkUserInfoMasked(user.user) ? `demo_channel_${ID}` : Name, // mask channel info if user is a demo account
      };
    });
  };

  // fetch a list of channel groups
  const onFetchChannelGroups = (params: IChannelsUiParams) => {
    const startTime = moment().unix();
    setLoadState(LoadingStateLoading);
    if (!isEqual(uiParams, params)) {
      updateCfg(params);
      setUiParams(params);
    }
    return fetchChannelGroups(session.publisher.ID, formatParams(params))
      .then(({ ChannelGroups, TotalRows, Labels }) => {
        setChannelGroups(formatChannelGroups(ChannelGroups));
        setTotalRows(TotalRows);
        setLabels(Labels);
        setLoadState(LoadingStateHasData);
        const loadTime = moment().unix() - startTime;
        track(
          'Channels - Groups - List Loaded',
          {
            isSuperUser,
            loadTime,
            numRowsDisplayed: ChannelGroups.length,
            totalRowCount: TotalRows,
          },
          user.user,
        );
      })
      .catch((err) => {
        setError(err);
        setSnackbarMessage('Error retrieving data.');
        setLoadState(LoadingStateNoData);
      });
  };

  // fetch a list of channels - to be map back to the channels in each channel group
  const onFetchChannels = () => {
    setLoadState(LoadingStateLoading);
    return fetchChannels(session.publisher.ID, {
      preloads: [],
      uiFields: ['id', 'name'],
    })
      .then(({ Channels }) => {
        setChannels(formatChannels(Channels));
        setLoadState(LoadingStateHasData);
      })
      .catch((err) => {
        setError(err);
        setSnackbarMessage('Error retrieving data.');
        setLoadState(LoadingStateNoData);
      });
  };

  // delete one or many channel groups
  const onDeleteChannelGroups = (channelGroupIDs: string[]) => {
    const isBulkDelete = channelGroupIDs.length > 1;
    setIsActionPending(true);
    return Promise.all(channelGroupIDs.map((id) => deleteChannelGroup(session.publisher.ID, id)))
      .then(() => {
        track(
          `Channels - Groups - ${isBulkDelete ? 'Bulk Delete' : 'Delete Channel'}`,
          { isSuperUser },
          user.user,
        );
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Successfully deleted ${
              isBulkDelete ? `${channelGroupIDs.length} channel groups` : 'channel group'
            }.`,
          );
          setError(null);
        });
        onFetchChannelGroups(uiParams);
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(`Error deleting channel groups`);
          setError(err);
        });
      });
  };

  // update one or many channel groups
  const onUpdateChannelGroups = (
    channelGroupIDs: string[],
    updates: { [key: string]: Partial<IChannelGroupRow> },
  ) => {
    if (Object.values(updates).some((update) => isEmpty(update.ChannelIDs))) {
      setSnackbarMessage('Channel group must have at least one member channel');
      setError({ error: true });
      return;
    }
    const isBulkUpdate = channelGroupIDs.length > 1;
    setIsActionPending(true);
    return Promise.all(
      channelGroupIDs.map((channelID) =>
        updateChannelGroup(session.publisher.ID, updates[channelID]),
      ),
    )
      .then(() => {
        track(
          `Channel Group - ${isBulkUpdate ? 'Bulk Delete' : 'Delete Channel'}`,
          { isSuperUser },
          user.user,
        );
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Successfully updated ${
              isBulkUpdate
                ? `${channelGroupIDs.length} channel groups`
                : `channel group [${channelGroupIDs[0]}]`
            }.`,
          );
          setError(null);
        });
        onFetchChannelGroups(uiParams);
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Error updating ${
              isBulkUpdate
                ? `${channelGroupIDs.length} channel groups`
                : `channel group [${channelGroupIDs[0]}]`
            }.`,
          );
          setError(err);
        });
      });
  };

  // create a new channel group
  const onCreateChannelGroups = (channelGroups: Array<Partial<IChannelGroupRow>>) => {
    if (channelGroups.some((group) => group.ChannelIDs === '')) {
      setSnackbarMessage('Channel group must have at least one member channel');
      setError({ error: true });
      return;
    }
    const isBulkUpdate = channelGroups.length > 1;
    setIsActionPending(true);
    return Promise.all(
      channelGroups.map((group) => createChannelGroup(session.publisher.ID, group)),
    )
      .then(() => {
        track(
          `Channels - Groups - ${isBulkUpdate ? 'Bulk Delete' : 'Delete Channel'}`,
          { isSuperUser },
          user.user,
        );
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Successfully created ${
              isBulkUpdate ? `${channelGroups.length} channel groups` : 'channel group'
            }.`,
          );
          setError(null);
        });
        onFetchChannelGroups(uiParams);
      })
      .catch((err) => {
        unstable_batchedUpdates(() => {
          setIsActionPending(false);
          setSnackbarMessage(
            `Error creating ${
              isBulkUpdate ? `${channelGroups.length} channel groups` : 'channel group'
            }.`,
          );
          setError(err);
        });
      });
  };

  useEffect(() => {
    onFetchChannels();
  }, []);

  return (
    <>
      <ChannelGroups
        createChannelGroups={onCreateChannelGroups}
        deleteChannelGroups={onDeleteChannelGroups}
        updateChannelGroups={onUpdateChannelGroups}
        cfg={cfg}
        updateCfg={updateCfg}
        fetchChannelGroups={onFetchChannelGroups}
        isActionPending={isActionPending}
        channels={channels}
        channelGroups={channelGroups}
        labels={labels}
        totalRows={totalRows}
        loadState={loadState}
      />
      <SnackbarView open={!!snackbarMessage} error={error} onClose={() => setSnackbarMessage('')}>
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
